import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

// Types & Enums
import { ButtonThemes } from '../../../../enums/button-themes';
import { NotificationMessageType } from '../../../../enums/notification-types';
import { FieldValues } from 'react-hook-form';
import validators from '../../../../components/forms/validation/validators';
import type { ImpressionModel, ImpressionModelConfig } from '../manage-impression-modelling';

// Redux
import { setModal } from '../../../../redux/actions/modal';
import { useDispatch } from 'react-redux';
import { removePopup, setPopup } from '../../../../redux/actions/popup';
import { addNotification } from '../../../../redux/actions/notification';

// Helpers
import { generatePath, generateUrl, generateUrlDetail } from '../../../../helpers/request-builder';
import {
    CONFIG_CHANNELS,
    CONFIG_DASH_REFERER,
    IMPRESSION_MODEL_RESOURCE,
    IMPRESSION_MODEL_CONFIG_RESOURCE,
} from '../../../../configurations/resources-config';

// Components
import ConfigTable from '../../../../components/tables/config-table/config-table';
import Form from '../../../../components/forms/form';

const StyledSection = styled.div``;

const StyledH2 = styled.h2`
    font-size: 1.1rem;
`;

const StyledFormContainer = styled.div`
    background-color: ${props => props.theme.colours.white};
    padding: 20px;
    margin-top: 20px;
`;

type DropdownOption = {
    label: string;
    value: string;
};

type LayoutImpressionModelConfigsProps = {
    data: ImpressionModelConfig[];
    sources: ImpressionModel[];
    onDelete?: () => void;
    onEdit?: () => void;
    isRefreshing?: boolean;
};

const LayoutImpressionModelConfigs = ({
    data,
    sources,
    onDelete,
    onEdit,
    isRefreshing,
}: LayoutImpressionModelConfigsProps) => {
    const dispatch = useDispatch();

    const [configs, setConfigs] = useState<ImpressionModelConfig[]>([]);
    const [configsToDelete, setConfigsToDelete] = useState<number[]>([]);
    const [configToEdit, setConfigToEdit] = useState<ImpressionModelConfig | null>(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const [formDefaultValues, setFormDefaultValues] = useState({ source: '', channel: '', active: true });
    const [editFormValues, setEditFormValues] = useState({ source: '', channel: '', active: true });
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPatching, setIsPatching] = useState(false);
    const [sourceOptions, setSourceOptions] = useState<DropdownOption[]>([]);
    const [channelOptions, setChannelOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        if (data) {
            setConfigs(data);
        }
    }, [data]);

    // Fetch source and channel dropdown options for edit form
    useEffect(() => {
        Promise.all([
            axios({
                method: 'GET',
                url: generateUrl(IMPRESSION_MODEL_RESOURCE.category, IMPRESSION_MODEL_RESOURCE.id, [
                    { key: 'limit', value: 0 },
                ]),
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }),
            axios({
                method: 'GET',
                url: generateUrl(CONFIG_DASH_REFERER.category, CONFIG_DASH_REFERER.id, [
                    { key: 'active', value: 1 },
                    { key: 'order_by', value: 'name' },
                    { key: 'limit', value: 0 },
                ]),
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            }),
        ]).then(([sourceResponse, channelResponse]) => {
            setSourceOptions(
                sourceResponse.data.objects.map((obj: { id: number; value: string }) => ({
                    label: obj.value,
                    value: String(obj.id),
                }))
            );
            setChannelOptions(
                channelResponse.data.objects.map((obj: { id: number; name: string }) => ({
                    label: obj.name,
                    value: String(obj.id),
                }))
            );
        });
    }, []);

    const handleDeleteConfigs = (ids: number[]) => {
        setIsDeleting(true);
        const deleteRequests = ids.map(id => {
            const url = generateUrlDetail(
                IMPRESSION_MODEL_CONFIG_RESOURCE.category,
                IMPRESSION_MODEL_CONFIG_RESOURCE.id,
                id.toString()
            );
            if (!url) throw new Error('Invalid URL');
            return axios({
                method: 'DELETE',
                url,
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            });
        });

        Promise.all(deleteRequests)
            .then(() => {
                setConfigsToDelete([]);
                dispatch(
                    addNotification({
                        copy: 'Impression configuration successfully deleted.',
                        type: NotificationMessageType.Success,
                    })
                );
                if (onDelete) onDelete();
            })
            .catch(() => {
                dispatch(
                    addNotification({
                        copy: 'There was an issue while deleting. Please try again later or contact support@cubed.email.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsDeleting(false));
    };

    const handlePatchConfig = (payload: Record<string, any>[], resourceIds: string[]) => {
        setIsPatching(true);
        const patchRequests = resourceIds.map((id, index) => {
            const url = generateUrlDetail(
                IMPRESSION_MODEL_CONFIG_RESOURCE.category,
                IMPRESSION_MODEL_CONFIG_RESOURCE.id,
                id
            );
            if (!url) throw new Error('Invalid URL for PATCH');
            return axios({
                method: 'PATCH',
                url,
                data: payload[index],
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            });
        });

        Promise.all(patchRequests)
            .then(() => {
                setShowEditForm(false);
                dispatch(
                    addNotification({
                        copy: 'Impression configuration successfully updated.',
                        type: NotificationMessageType.Success,
                    })
                );
                if (onEdit) onEdit();
            })
            .catch(() => {
                dispatch(
                    addNotification({
                        copy: 'There was an issue while updating. Please try again later or contact support@cubed.email.',
                        type: NotificationMessageType.Error,
                    })
                );
            })
            .finally(() => setIsPatching(false));
    };

    const handleCheckboxChange = (rowId: number) => {
        const updatedConfigs = configs.map(config => {
            if (config.id === rowId) {
                return { ...config, selected: !config.selected };
            }
            return config;
        });
        setConfigs(updatedConfigs);
        setConfigsToDelete(updatedConfigs.filter(c => c.selected).map(c => c.id));
    };

    const handleEditClick = (rowId: number) => {
        const config = configs.find(c => c.id === rowId);
        if (config) {
            const defaults = {
                source: String(config.impressionModelId),
                channel: String(config.refererId),
                active: config.active,
            };
            setConfigToEdit(config);
            setFormDefaultValues(defaults);
            setEditFormValues(defaults);
            setShowEditForm(true);
        }
    };

    const handleSingleDeleteClick = (rowId: number) => {
        setConfigsToDelete([rowId]);

        const handleDiscardClick = () => {
            setConfigsToDelete([]);
            dispatch(removePopup());
        };

        const handleDelete = () => {
            handleDeleteConfigs([rowId]);
            dispatch(removePopup());
        };

        dispatch(
            setPopup({
                title: 'Delete',
                iconType: 'warning',
                contentType: 'simple',
                config: {
                    copy: 'Are you sure you would like to delete this impression configuration?',
                },
                buttons: [
                    {
                        onClick: handleDelete,
                        value: 'YES, DELETE',
                    },
                    {
                        onClick: handleDiscardClick,
                        value: 'CANCEL',
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ],
            })
        );
    };

    const handleBulkDeleteClick = () => {
        const handleDiscardClick = () => {
            dispatch(removePopup());
        };

        const handleDelete = () => {
            handleDeleteConfigs(configsToDelete);
            dispatch(removePopup());
        };

        dispatch(
            setPopup({
                title: 'Delete',
                iconType: 'warning',
                contentType: 'simple',
                config: {
                    copy: 'Are you sure you would like to delete the selected impression configurations?',
                },
                buttons: [
                    {
                        onClick: handleDelete,
                        value: 'YES, DELETE',
                    },
                    {
                        onClick: handleDiscardClick,
                        value: 'CANCEL',
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ],
            })
        );
    };

    const handleFormSubmit = (data: FieldValues) => {
        const isDuplicateEdit = configs.some(
            config =>
                config.id !== configToEdit!.id &&
                config.impressionModelId === Number(data.source) &&
                config.refererId === Number(data.channel)
        );

        if (isDuplicateEdit) {
            dispatch(
                addNotification({
                    copy: 'This source and channel combination already exists.',
                    type: NotificationMessageType.Error,
                })
            );
            return;
        }

        handlePatchConfig(
            [
                {
                    impression_model: generatePath(
                        IMPRESSION_MODEL_RESOURCE.category,
                        IMPRESSION_MODEL_RESOURCE.id,
                        data.source
                    ),
                    referer: generatePath(CONFIG_CHANNELS.category, CONFIG_CHANNELS.id, data.channel),
                    active: data.active,
                },
            ],
            [configToEdit!.id.toString()]
        );
    };

    const noSourcesExist = sources.length === 0;

    return (
        <StyledSection>
            <StyledH2>Impression Model Configurations</StyledH2>
            <p>Manage the mappings between impression sources and channels.</p>

            <ConfigTable
                status={isRefreshing ? 'pending' : 'success'}
                isFetching={isDeleting || isPatching || !!isRefreshing}
                disabled={isDeleting || !!isRefreshing}
                empty={configs.length === 0}
            >
                <ConfigTable.Table maxHeight="200px" emptyMessage="No impression configurations found">
                    <ConfigTable.Header>
                        <ConfigTable.Row key="impression-config-header">
                            <ConfigTable.CellHeader />
                            <ConfigTable.CellHeader>Source</ConfigTable.CellHeader>
                            <ConfigTable.CellHeader>Channel</ConfigTable.CellHeader>
                            <ConfigTable.CellHeader>Active</ConfigTable.CellHeader>
                            <ConfigTable.CellHeader />
                        </ConfigTable.Row>
                    </ConfigTable.Header>
                    <ConfigTable.Body>
                        {configs.map(config => (
                            <ConfigTable.Row key={`impression-config-${config.id}`}>
                                <ConfigTable.CellCheckbox
                                    rowId={config.id}
                                    checked={config.selected}
                                    onCheckedChange={handleCheckboxChange}
                                />
                                <ConfigTable.Cell>{config.sourceName}</ConfigTable.Cell>
                                <ConfigTable.Cell>{config.channelName}</ConfigTable.Cell>
                                <ConfigTable.Cell>{config.active ? 'YES' : 'NO'}</ConfigTable.Cell>
                                <ConfigTable.CellActions>
                                    <ConfigTable.ActionDropdownItem
                                        rowId={config.id}
                                        type="edit"
                                        label="Edit"
                                        onClick={handleEditClick}
                                    />
                                    <ConfigTable.ActionDropdownItem
                                        rowId={config.id}
                                        type="delete"
                                        label="Delete"
                                        onClick={handleSingleDeleteClick}
                                    />
                                </ConfigTable.CellActions>
                            </ConfigTable.Row>
                        ))}
                    </ConfigTable.Body>
                </ConfigTable.Table>
                <ConfigTable.ActionBar>
                    <ConfigTable.ActionButton
                        type="delete"
                        label="Delete"
                        onClick={handleBulkDeleteClick}
                        isDisabled={configsToDelete.length === 0}
                    />
                    <ConfigTable.ActionButton
                        type="add"
                        label="Add Configuration"
                        onClick={() => dispatch(setModal('AddImpressionModelConfig', {}))}
                        isDisabled={noSourcesExist}
                    />
                </ConfigTable.ActionBar>
            </ConfigTable>

            {showEditForm && configToEdit && (
                <StyledFormContainer>
                    <Form
                        status={isPatching ? 'loading' : 'enabled'}
                        onSubmit={handleFormSubmit}
                        defaultValues={formDefaultValues}
                        onWatchFieldsChange={fields =>
                            setEditFormValues({
                                source: fields.source ?? '',
                                channel: fields.channel ?? '',
                                active: fields.active ?? true,
                            })
                        }
                    >
                        <Form.Body>
                            <Form.Section>
                                <Form.SectionTitle>Edit Impression Configuration</Form.SectionTitle>

                                <Form.Field>
                                    <Form.InputLabel htmlFor="source" label="Source" required={true} />
                                    <Form.InputSelect
                                        name="source"
                                        options={sourceOptions}
                                        placeholder="Select a source"
                                        validators={[validators.required]}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <Form.InputLabel htmlFor="channel" label="Channel" required={true} />
                                    <Form.InputSelect
                                        name="channel"
                                        options={channelOptions}
                                        placeholder="Select a channel"
                                        validators={[validators.required]}
                                    />
                                </Form.Field>

                                <Form.Field>
                                    <Form.InputToggle name="active" label="Active" defaultOn={configToEdit.active} />
                                </Form.Field>
                            </Form.Section>
                        </Form.Body>
                        <Form.Footer>
                            <Form.InputButton
                                type="submit"
                                value="Save"
                                loading={isPatching}
                                disabled={
                                    !editFormValues.source ||
                                    !editFormValues.channel ||
                                    (editFormValues.source === formDefaultValues.source &&
                                        editFormValues.channel === formDefaultValues.channel &&
                                        editFormValues.active === formDefaultValues.active)
                                }
                            />
                            <Form.InputButton
                                type="button"
                                value="Cancel"
                                buttonTheme={ButtonThemes.Secondary}
                                onClick={() => setShowEditForm(false)}
                            />
                        </Form.Footer>
                    </Form>
                </StyledFormContainer>
            )}
        </StyledSection>
    );
};

export default LayoutImpressionModelConfigs;
